// hooks/quoter/usePricing.ts
import { useMemo } from 'react';
import type { QuoteData } from '../../types/quoter/types';

export interface PricingBreakdown {
    hasDiscounts: boolean;
    hasReseller: boolean;
    retailPrice: number;
    customerDiscountPercent: number;
    customerDiscountAmount: number;
    customerPrice: number;
    resellerDiscountPercent: number;
    resellerDiscountAmount: number;
    resellerPrice: number;
    finalPrice: number; // The actual price the user pays
    showRetailPrice: boolean;
    showCustomerDiscount: boolean;
    showResellerSection: boolean;
}

export const usePricing = (quoteData: QuoteData | null): PricingBreakdown | null => {
    return useMemo(() => {
        if (!quoteData) return null;

        const hasCustomerDiscount = quoteData.CustomerDiscountPercent !== 0;
        const hasReseller = quoteData.ResaleUserPrice !== 0;
        const hasDiscounts = hasCustomerDiscount || hasReseller;

        const retailPrice = quoteData.CalculatedPrice || 0;
        const customerDiscountPercent = quoteData.CustomerDiscountPercent || 0;
        const customerDiscountAmount = quoteData.CustomerDiscountApplied || 0;
        const customerPrice = quoteData.QuotedPrice || 0;
        const resellerDiscountPercent = quoteData.ResaleDiscountPercent || 0;
        const resellerDiscountAmount = quoteData.ResaleDiscountApplied || 0;
        const resellerPrice = quoteData.ResaleUserPrice || 0;

        // Final price is reseller price if available, otherwise customer price
        const finalPrice = resellerPrice > 0 ? resellerPrice : customerPrice;

        return {
            hasDiscounts,
            hasReseller,
            retailPrice,
            customerDiscountPercent,
            customerDiscountAmount,
            customerPrice,
            resellerDiscountPercent,
            resellerDiscountAmount,
            resellerPrice,
            finalPrice,
            showRetailPrice: hasDiscounts,
            showCustomerDiscount: hasCustomerDiscount,
            showResellerSection: hasReseller,
        };
    }, [quoteData]);
};

